import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PathologyLab } from '../../types';
import { LabCard } from '../cards/LabCard';
import { PlatformTargetIcon } from '../icons/PlatformTargetIcon';
import { AddonToolIcon } from '../icons/AddonToolIcon';

interface PathSplitAnimationProps {
  labs: PathologyLab[];
  pathType: 'platform' | 'addon';
  isActive: boolean;
  onComplete?: () => void;
}

export const PathSplitAnimation: React.FC<PathSplitAnimationProps> = ({
  labs,
  pathType,
  isActive,
  onComplete,
}) => {
  const [showLabels, setShowLabels] = useState(false);
  const [animationStage, setAnimationStage] = useState<'initial' | 'splitting' | 'complete'>('initial');

  // Filter labs by type
  const platformLabs = labs.filter(lab => lab.labType === 'platform');
  const addonLabs = labs.filter(lab => lab.labType === 'addon');

  // Determine which set to show
  const displayLabs = pathType === 'platform' ? platformLabs : addonLabs;

  useEffect(() => {
    if (isActive) {
      // Stage 1: Show labels
      setTimeout(() => setShowLabels(true), 200);

      // Stage 2: Begin split animation
      setTimeout(() => setAnimationStage('splitting'), 400);

      // Stage 3: Complete
      setTimeout(() => {
        setAnimationStage('complete');
        onComplete?.();
      }, 1600);
    } else {
      setShowLabels(false);
      setAnimationStage('initial');
    }
  }, [isActive, onComplete]);

  if (!isActive) return null;

  return (
    <div className="relative w-full min-h-screen">
      {/* Path Labels */}
      <AnimatePresence>
        {showLabels && (
          <div className="absolute top-8 left-0 right-0 z-10 flex justify-between px-16">
            {/* Platform Path Label */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className={`text-center ${pathType === 'platform' ? 'opacity-100' : 'opacity-40'}`}
            >
              <div className="flex items-center justify-center gap-2 text-2xl font-bold text-forest-green mb-2">
                <PlatformTargetIcon size={24} />
                Platform Candidates
              </div>
              <div className="text-sm text-light-gray">
                {platformLabs.length} Labs • Acquisition Targets
              </div>
            </motion.div>

            {/* Add-on Path Label */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className={`text-center ${pathType === 'addon' ? 'opacity-100' : 'opacity-40'}`}
            >
              <div className="flex items-center justify-center gap-2 text-2xl font-bold text-light-blue mb-2">
                <AddonToolIcon size={24} />
                Add-on Candidates
              </div>
              <div className="text-sm text-light-gray">
                {addonLabs.length} Labs • Bolt-on Acquisitions
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Animated Cards */}
      <div className="pt-32 px-8">
        <AnimatePresence mode="wait">
          {animationStage !== 'initial' && (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {displayLabs.slice(0, 25).map((lab, index) => {
                // Calculate split direction based on type
                const initialX = 0;

                return (
                  <motion.div
                    key={lab.id}
                    initial={{
                      opacity: 0,
                      scale: 0.8,
                      x: initialX,
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      x: 0,
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                    }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.03,
                      ease: [0.43, 0.13, 0.23, 0.96], // Cubic bezier easing
                    }}
                  >
                    <LabCard
                      lab={lab}
                      variant={
                        lab.labType === 'platform'
                          ? 'selected'
                          : 'default'
                      }
                    />
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Show count if more than 25 */}
        {displayLabs.length > 25 && animationStage === 'complete' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-center mt-8 text-light-gray"
          >
            Showing top 25 of {displayLabs.length} {pathType} candidates
          </motion.div>
        )}
      </div>

      {/* Background Gradient Effect */}
      <div
        className={`fixed inset-0 pointer-events-none transition-opacity duration-1000 ${
          showLabels ? 'opacity-20' : 'opacity-0'
        }`}
        style={{
          background: pathType === 'platform'
            ? 'radial-gradient(circle at 25% 50%, rgba(46, 125, 50, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 75% 50%, rgba(91, 155, 213, 0.1) 0%, transparent 50%)',
        }}
      />
    </div>
  );
};
